import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackDownload, trackEvent } from '../utils/analytics';

const LearningCenter = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLearningCenterDocuments();
    // Track learning center page view
    trackFeatureUsage('Learning Center', 'view');
  }, []);

  const fetchLearningCenterDocuments = async () => {
    try {
      const response = await axios.get('/api/documents/category/LEARNING_CENTER');
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch learning center documents:', error);
      setError('Failed to load learning center documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      // Track download attempt
      const fileExtension = fileName.split('.').pop().toLowerCase();
      trackDownload(fileName, fileExtension);
      
      const response = await axios.get(`/api/documents/download/${documentId}`, {
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Track successful download
      trackEvent('document_download_success', {
        event_category: 'Learning Center',
        event_label: fileName,
        document_category: 'LEARNING_CENTER'
      });
    } catch (error) {
      console.error('Failed to download document:', error);
      setError('Failed to download document. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (contentType, isVideoLink) => {
    if (isVideoLink) return '🎥';
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('image')) return '🖼️';
    if (contentType?.includes('video')) return '🎥';
    if (contentType?.includes('audio')) return '🎵';
    if (contentType?.includes('word')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) return '📋';
    return '📁';
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'LEARNING_CENTER': return 'Learning Center';
      default: return category;
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading learning center...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Learning Center</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Access training materials and educational resources
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            📚 Learning Resources ({documents.length} documents)
          </h3>
        </div>

        <div style={{ 
          marginBottom: '15px', 
          padding: '10px', 
          background: 'var(--pale-blue)', 
          borderRadius: '6px',
          fontSize: '14px',
          color: 'var(--dark-blue)'
        }}>
          📖 Access training materials, guides, and educational resources to enhance your skills and knowledge.
        </div>

        {documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No learning materials available at the moment. Check back later for new resources.
          </div>
        ) : (
          <>
            {/* Video Learning Section */}
            {documents.filter(doc => doc.isVideoLink).length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎥 Video Learning ({documents.filter(doc => doc.isVideoLink).length} videos)
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  {documents.filter(doc => doc.isVideoLink).map((document) => (
                    <div 
                      key={document.id}
                      style={{
                        border: '1px solid var(--gray-medium)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: 'var(--white)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      onClick={() => window.open(document.videoUrl, '_blank')}
                    >
                      {/* Video Thumbnail */}
                      <div style={{ 
                        position: 'relative', 
                        paddingBottom: '56.25%', 
                        height: 0,
                        background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)'
                      }}>
                        {document.videoThumbnailUrl ? (
                          <img 
                            src={document.videoThumbnailUrl}
                            alt="Video thumbnail"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: document.videoThumbnailUrl ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                          fontSize: '48px',
                          color: 'white'
                        }}>
                          🎥
                        </div>
                        {/* Play Button Overlay */}
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '70px',
                          height: '70px',
                          background: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                          e.target.style.transform = 'translate(-50%, -50%) scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                          e.target.style.transform = 'translate(-50%, -50%) scale(1)';
                        }}
                        >
                          ▶️
                        </div>
                        {/* Learning Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'var(--success)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          📚 LEARNING
                        </div>
                      </div>
                      
                      {/* Video Info */}
                      <div style={{ padding: '15px' }}>
                        <div style={{ 
                          fontWeight: '600', 
                          marginBottom: '8px',
                          fontSize: '15px',
                          lineHeight: '1.4',
                          color: 'var(--dark-blue)'
                        }}>
                          {document.description || document.originalFileName}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: 'var(--gray-dark)',
                          marginBottom: '12px'
                        }}>
                          Added by {document.createdBy} • {new Date(document.createdAt).toLocaleDateString()}
                        </div>
                        
                        {/* Watch Button */}
                        <div style={{ 
                          background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          textAlign: 'center',
                          fontSize: '13px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}>
                          🎥 Watch Now
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents Section */}
            {documents.filter(doc => !doc.isVideoLink).length > 0 && (
              <div>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📁 Learning Documents ({documents.filter(doc => !doc.isVideoLink).length} files)
                </h4>
                <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Document
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Description
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Size
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Uploaded
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.filter(doc => !doc.isVideoLink).map((document, index) => (
                  <tr 
                    key={document.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>
                          {getFileIcon(document.contentType, document.isVideoLink)}
                        </span>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {document.originalFileName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {document.contentType}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {document.description || 'No description'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatFileSize(document.fileSize)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>
                        {new Date(document.createdAt).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        by {document.createdBy}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button 
                        className="btn btn-primary"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                        onClick={() => handleDownload(document.id, document.originalFileName)}
                        title="Download Document"
                      >
                        ⬇️ Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </>
    )}
      </div>
    </div>
  );
};

export default LearningCenter;
